import { fmt } from "../format/number";
import type { Section } from "../geometry/section";
import { rcFrame } from "../geometry/rcCantilever";
import type { CaseLoads, CaseParams } from "../loads/assemble";
import type { CaseId } from "../types/result";
import type { BarSpec, RcCantileverGeometry, RcDesignCode, WallInput } from "../types/input";
import type { WarningCode } from "../warnings";
import { RC_PROFILES, minFlexuralArea, rcCodeFor, type RcCodeProfile, type RcCombination, type RcMaterials } from "./codes";
import { checkShear, designFlexure, manualBars, momentCapacity, selectBars, type BarChoice } from "./section";
import { designCaseFor, factored, heelDesignEffects, keyEffects, stemEffects, toeDesignEffects, type ClassEffects, type ClassSums, type DesignCase } from "./cantilever";
import { counterfortGeometry, heelSlabLoad, stemSlabStrips, type SlabStrip } from "./counterfort";
import { barArea } from "./section";

export type RcLocation = "stem" | "toe" | "heel" | "key" | "stemSlab" | "heelSlab" | "counterfort";
export type TensionFace = "back" | "front" | "top" | "bottom";
export type RcBarPosition = "stemMain" | "stemFront" | "stemHorizontal" | "toeMain" | "heelMain" | "baseDistribution" | "keyMain"
  | "stemHorizontalSupport" | "stemHorizontalSpan" | "heelLongTop" | "heelLongBottom" | "counterfortMain" | "counterfortTieH" | "counterfortTieV";

export interface RcDesignCaseResult { id: string; label: string; caseId: CaseId; kind: "static" | "seismic"; Nd: number; eD: number; qToe: number; qHeel: number; contactLength: number }

export interface RcSectionResult {
  id: string; label: string; location: RcLocation; y?: number;
  b: number; h: number; d: number; face: TensionFace;
  Md: number; Vd: number; governingM: string; governingV: string;
  /** Hükmeden kombinasyonda karakteristik moment bileşenleri (kNm/m) — rapor */
  Mk: ClassSums; Vk: ClassSums;
  AsReq: number; AsMin: number; AsNeeded: number; AsProv: number; bar: { dia: number; spacing: number; manual: boolean };
  Mr: number; ratio: number; cOverD: number; cOverDMax: number; ductile: boolean;
  shear: { Vd: number; Vr: number; ratio: number; ok: boolean; formula: string };
  ok: boolean; verdict: string;
}

export interface RcReinforcementItem {
  position: RcBarPosition; label: string; face: string;
  bar: { dia: number; spacing: number }; As: number; AsNeeded: number; manual: boolean; ok: boolean;
  /** Payanda ana donatısı: adet (aralık yerine) */
  count?: number;
  /** Kenetlenme boyu (m) */
  lb: number;
  /** Gövde ana donatısı: yarısının kesilebileceği kot (m) ve uzatma; yoksa tümü tepeye */
  cutoff?: { level: number; extend: number; note: string };
  note?: string;
}

export interface RcDesignResult {
  code: RcDesignCode; codeLabel: string; references: string[]; loadFactorNote: string;
  materials: RcMaterials; cover: number;
  combinations: RcCombination[]; designCases: RcDesignCaseResult[];
  stem: RcSectionResult[]; toe: RcSectionResult | null; heel: RcSectionResult | null; key: RcSectionResult | null;
  reinforcement: RcReinforcementItem[];
  /** Payandalı duvar: plak ve payanda sonuçları */
  counterfort?: RcCounterfortResult;
  ok: boolean; notes: string[];
  formulas: { flexure: string; shear: string; development: string };
}

export interface RcCounterfortResult {
  spacing: number; thickness: number; clearSpan: number; alphaDeg: number;
  /** Gövde plağı şeritleri (faktörlü basınç, kNm/m yükseklik) */
  stemSlab: SlabStrip[];
  /** Alt şerit düşey konsol: yükseklik a ve momenti */
  verticalStrip: { a: number; Md: number };
  heelSlab: { w: number; x: number; Msupport: number; Mspan: number; V: number; caseId: string };
  /** Payanda kesitleri (kot bazlı) */
  sections: RcSectionResult[];
  ties: { horizontal: { T: number; As: number }; vertical: { T: number; As: number } };
  formulas: string[];
}

export interface RcDesignContext {
  input: WallInput; section: Section; cases: CaseParams[]; loads: Partial<Record<CaseId, CaseLoads>>;
  warn: (code: WarningCode, context?: string) => void;
}

const ASSUMED_DIA = 16;   // d = h − pas payı − φ/2 için ilk kabul; seçimden sonra yenilenir

interface Effects { Md: number; Vd: number; caseM: string; caseV: string; Mk: ClassSums; Vk: ClassSums }

/** Karakteristik (sınıflara ayrılmış) tesirler → tüm tasarım durumlarında en büyük M ve V */
function governingOf(list: { dc: DesignCase; e: ClassEffects }[]): Effects {
  let best: Effects | null = null;
  let bestV = -Infinity, caseV = "";
  for (const { dc, e } of list) {
    const Md = factored(e.M, dc.combo), Vd = factored(e.V, dc.combo);
    if (!best || Math.abs(Md) > Math.abs(best.Md)) best = { Md, Vd, caseM: dc.id, caseV: dc.id, Mk: e.M, Vk: e.V };
    if (Math.abs(Vd) > bestV) { bestV = Math.abs(Vd); caseV = dc.id; }
  }
  if (!best) return { Md: 0, Vd: 0, caseM: "—", caseV: "—", Mk: { G: 0, Q: 0, H: 0, F: 0, E: 0 }, Vk: { G: 0, Q: 0, H: 0, F: 0, E: 0 } };
  return { ...best, Vd: bestV, caseV };
}

export function designRcWall(ctx: RcDesignContext): RcDesignResult {
  const { input, section, cases, loads, warn } = ctx;
  const g = input.geometry as RcCantileverGeometry;
  const f = rcFrame(g);
  const rc = input.rc;
  const code = rcCodeFor(rc.code, input.checks.designCode);
  const p = RC_PROFILES[code];
  const alphaCc = code === "ec2" && input.checks.ec.nationalAnnex !== "EN" ? 0.85 : 1.0;
  const m = p.materials(rc.concrete, rc.steel, { alphaCc });
  const notes: string[] = [];
  const manualUsed: string[] = [];

  // Tasarım durumları: kombinasyon × yük durumu
  const designCases: DesignCase[] = [];
  for (const cp of cases) {
    const cl = loads[cp.id];
    if (!cl) continue;
    for (const combo of p.combinations(cp.id !== "static", input.checks.ec.psi2)) designCases.push(designCaseFor(cl, cp.id, combo, f.B));
  }
  const cpOf = (id: CaseId): CaseParams => cases.find((c) => c.id === id)!;

  const mkSection = (id: string, label: string, location: RcLocation, face: TensionFace, h: number, eff: Effects, pos: RcBarPosition, y?: number): { sec: RcSectionResult; choice: BarChoice & { ok: boolean; manual: boolean } } => {
    const b = 1;
    const manual = manualBar(rc.bars, pos);
    const dia0 = manual?.dia ?? ASSUMED_DIA;
    const design = (dia: number) => {
      const d = h - rc.cover - dia / 2000;
      const fl = designFlexure(p, m, Math.abs(eff.Md), b, d, h);
      const AsMin = minFlexuralArea(p, m, b, d, h);
      const AsNeeded = Math.max(fl.AsReq, AsMin);
      const sMax = p.maxSpacing(h).main;
      const choice = manual ? { ...manualBars(manual, AsNeeded, sMax), manual: true } : { ...selectBars(AsNeeded, sMax), manual: false };
      return { d, fl, AsMin, AsNeeded, choice };
    };
    let r = design(dia0);
    if (!manual && r.choice.dia !== dia0) r = design(r.choice.dia);
    const { d, fl, AsMin, AsNeeded, choice } = r;
    if (manual) manualUsed.push(label);
    const shear = checkShear(p, m, eff.Vd, b, d, choice.As);
    const Mr = momentCapacity(m, choice.As, b, d);
    const ratio = Mr > 0 ? Math.abs(eff.Md) / Mr : Infinity;
    if (!fl.ductile) warn("RC_RHO_MAX", label);
    if (!shear.ok) warn("RC_SHEAR_EXCEEDED", label);
    const ok = choice.ok && fl.ductile && shear.ok && ratio <= 1 + 1e-9;
    const verdict = `${ok ? "SAĞLIYOR" : "SAĞLAMIYOR"} — M_d = ${fmt1(Math.abs(eff.Md))} kNm/m, A_s,gerekli = ${fmt0(AsNeeded)} mm²/m, φ${choice.dia}/${fmtCm(choice.spacing)} (${fmt0(choice.As)} mm²/m), M_r = ${fmt1(Mr)} kNm/m; V_d = ${fmt1(shear.Vd)} ${shear.ok ? "≤" : ">"} V_r = ${fmt1(shear.Vr)} kN/m`;
    const sec: RcSectionResult = {
      id, label, location, ...(y !== undefined ? { y } : {}), b, h, d, face,
      Md: eff.Md, Vd: eff.Vd, governingM: eff.caseM, governingV: eff.caseV, Mk: eff.Mk, Vk: eff.Vk,
      AsReq: fl.AsReq, AsMin, AsNeeded, AsProv: choice.As, bar: { dia: choice.dia, spacing: choice.spacing, manual: choice.manual },
      Mr, ratio, cOverD: fl.cOverD, cOverDMax: fl.cOverDMax, ductile: fl.ductile, shear, ok, verdict,
    };
    return { sec, choice };
  };

  // ── Gövde: taban üstünden her stemLevelStep, en üst kot hariç
  const stemCtxWarn = (c: WarningCode): void => warn(c);
  const levels: number[] = [];
  for (let y = f.tf; y < f.yTop - 1e-6; y += rc.stemLevelStep) levels.push(Math.round(y * 1e6) / 1e6);
  const stemEffByLevel = levels.map((lv) => {
    const list = designCases.map((dc) => ({ dc, e: stemEffects({ input, g, section, cp: cpOf(dc.caseId), warn: stemCtxWarn }, lv) }));
    return governingOf(list);
  });
  // Payandalı duvar: gövde yatay plak; düşey donatı alt şerit konsolundan (a = min(L/4, h_s), M_v ≈ p_taban·a²/2)
  const cfg = counterfortGeometry(g);
  const isCf = !!cfg;
  const strips = isCf ? stemSlabStrips(input, g, section, cases, designCases, levels, stemCtxWarn) : [];
  const aStrip = isCf ? Math.min(cfg!.L / 4, f.hs) : 0;
  const zeroK = (): ClassSums => ({ G: 0, Q: 0, H: 0, F: 0, E: 0 });
  const effVertical: Effects = isCf ? { Md: (strips[0]!.p * aStrip * aStrip) / 2, Vd: strips[0]!.p * aStrip, caseM: "plak", caseV: "plak", Mk: zeroK(), Vk: zeroK() } : stemEffByLevel[0]!;
  // Ana donatı taban kesitine göre; üst kotlarda aynı çubukla kapasite/gereklilik
  const base = mkSection("stem-0", isCf ? `Gövde alt şerit düşey konsol (a = ${fmt2(aStrip)} m)` : `Gövde y = ${fmt2(f.tf)} m (taban)`, "stem", "back", f.tb, effVertical, "stemMain", f.tf);
  const stem: RcSectionResult[] = [base.sec];
  for (let i = 1; i < (isCf ? 0 : levels.length); i++) {
    const lv = levels[i]!, hh = stemThickness(g, lv);
    const eff = stemEffByLevel[i]!;
    const d = hh - rc.cover - base.choice.dia / 2000;
    const fl = designFlexure(p, m, Math.abs(eff.Md), 1, d, hh);
    const AsMin = minFlexuralArea(p, m, 1, d, hh);
    const AsNeeded = Math.max(fl.AsReq, AsMin);
    const shear = checkShear(p, m, eff.Vd, 1, d, base.choice.As);
    const Mr = momentCapacity(m, base.choice.As, 1, d);
    const ratio = Mr > 0 ? Math.abs(eff.Md) / Mr : Infinity;
    const ok = fl.ductile && shear.ok && ratio <= 1 + 1e-9 && base.choice.As + 1e-9 >= AsNeeded;
    stem.push({
      id: `stem-${i}`, label: `Gövde y = ${fmt2(lv)} m`, location: "stem", y: lv, b: 1, h: hh, d, face: "back",
      Md: eff.Md, Vd: eff.Vd, governingM: eff.caseM, governingV: eff.caseV, Mk: eff.Mk, Vk: eff.Vk,
      AsReq: fl.AsReq, AsMin, AsNeeded, AsProv: base.choice.As, bar: { ...base.sec.bar }, Mr, ratio, cOverD: fl.cOverD, cOverDMax: fl.cOverDMax, ductile: fl.ductile, shear, ok,
      verdict: `${ok ? "SAĞLIYOR" : "SAĞLAMIYOR"} — M_d = ${fmt1(Math.abs(eff.Md))} kNm/m, A_s,gerekli = ${fmt0(AsNeeded)} mm²/m, mevcut ${fmt0(base.choice.As)} mm²/m; V_d = ${fmt1(shear.Vd)} ${shear.ok ? "≤" : ">"} V_r = ${fmt1(shear.Vr)} kN/m`,
    });
  }
  // Kesme kotu: yarı donatının yettiği ilk kot + max(d, 12φ) ve kenetlenme (≥ lb) uzatma
  const lbMain = p.developmentLength(m, base.choice.dia).lb;
  let cutoff: RcReinforcementItem["cutoff"];
  const half = base.choice.As / 2;
  const iCut = stem.findIndex((s, i) => i > 0 && s.AsNeeded <= half + 1e-9);
  if (iCut > 0 && !base.sec.bar.manual) {
    const extend = Math.max(stem[iCut]!.d, 12 * (base.choice.dia / 1000), lbMain);
    cutoff = { level: stem[iCut]!.y!, extend, note: `Çubukların yarısı y = ${fmt2(stem[iCut]!.y!)} m kotundan itibaren gereksiz; teorik kesme noktasından ${fmt2(extend)} m (≥ d, 12φ, l_b) yukarı uzatılarak kesilebilir` };
  }

  // ── Burun ve topuk
  const reinforcement0: RcReinforcementItem[] = [];
  let toe: RcSectionResult | null = null, heel: RcSectionResult | null = null;
  type Choice = BarChoice & { ok: boolean; manual: boolean };
  let toeChoice: Choice | null = null, heelChoice: Choice | null = null;
  if (f.toe > 1e-9) {
    const list = designCases.map((dc) => { const r = toeDesignEffects({ input, g, section, cp: cpOf(dc.caseId), dc }); return { dc, Md: r.Md, Vd: r.Vd }; });
    const eff = pick(list);
    const r = mkSection("toe", `Burun (gövde ön yüzünde, L = ${fmt2(f.toe)} m)`, "toe", eff.Md >= 0 ? "bottom" : "top", f.tf, eff, "toeMain");
    toe = r.sec; toeChoice = r.choice;
  }
  let counterfort: RcCounterfortResult | undefined;
  const cfSections: { sec: RcSectionResult; choice: Choice }[] = [];
  if (f.heel > 1e-9 && !isCf) {
    const list = designCases.map((dc) => { const r = heelDesignEffects({ input, g, section, cp: cpOf(dc.caseId), dc }); return { dc, Md: r.Md, Vd: r.Vd }; });
    const eff = pick(list);
    const r = mkSection("heel", `Topuk (gövde arka yüzünde, L = ${fmt2(f.heel)} m)`, "heel", eff.Md >= 0 ? "top" : "bottom", f.tf, eff, "heelMain");
    heel = r.sec; heelChoice = r.choice;
    if (!rc.heelUplift) warn("RC_HEEL_NO_UPLIFT");
  }
  if (isCf && cfg) {
    const cf = cfg;
    const hl = heelSlabLoad(input, g, section, cases, designCases);
    const effOf = (Md: number, Vd: number, tag: string): Effects => ({ Md, Vd, caseM: tag, caseV: tag, Mk: zeroK(), Vk: zeroK() });
    // Topuk: gövdeye yakın enine şerit konsolu (a = L/4) → enine donatı; payandalar arası sürekli plak → boyuna üst (mesnet) / alt (açıklık)
    const aHeel = Math.min(cf.L / 4, f.heel);
    const rh = mkSection("heel", `Topuk enine şerit konsolu (a = ${fmt2(aHeel)} m, w = ${fmt1(hl.w)} kN/m²)`, "heel", "top", f.tf, effOf((hl.w * aHeel * aHeel) / 2, hl.w * aHeel, hl.caseId), "heelMain");
    heel = rh.sec; heelChoice = rh.choice;
    const hs1 = mkSection("heel-support", `Topuk plağı payanda mesnedi (M⁻ = w·L²/12, L = ${fmt2(cf.L)} m)`, "heelSlab", "top", f.tf, effOf(hl.Msupport, hl.V, hl.caseId), "heelLongTop");
    const hs2 = mkSection("heel-span", "Topuk plağı açıklık (M⁺ = w·L²/16)", "heelSlab", "bottom", f.tf, effOf(hl.Mspan, hl.V, hl.caseId), "heelLongBottom");
    // Gövde plağı: taban şeridi hükmeder (en büyük basınç); mesnet arka yüz, açıklık ön yüz yatay donatı
    const st0 = strips[0]!;
    const ss1 = mkSection("stemslab-support", `Gövde plağı payanda mesnedi (M⁻ = p·L²/12, p = ${fmt1(st0.p)} kN/m²)`, "stemSlab", "back", f.tb, effOf(st0.Msupport, st0.V, "plak"), "stemHorizontalSupport");
    const ss2 = mkSection("stemslab-span", "Gövde plağı açıklık (M⁺ = p·L²/16)", "stemSlab", "front", f.tb, effOf(st0.Mspan, st0.V, "plak"), "stemHorizontalSpan");
    cfSections.push(hs1, hs2, ss1, ss2);
    // Payanda: T kiriş konsol, her kotta M = M_konsol(y)·s, V = H(y)·s; d = w(y)·sinα − pas payı; b = s (flanş = gövde), blok gövde kalınlığını aşmamalı
    const sections: RcSectionResult[] = [];
    const bTop = g.counterforts!.topWidth;
    for (let i = 0; i < levels.length; i++) {
      const y = levels[i]!, eff = stemEffByLevel[i]!;
      const wY = f.heel - (f.heel - bTop) * ((y - f.tf) / f.hs);
      const d = wY * Math.sin(cf.alpha) - rc.cover;
      if (d <= 0.05) continue;
      const Md = Math.abs(eff.Md) * cf.s, Vd = eff.Vd * cf.s;
      const fl = designFlexure(p, m, Md, cf.s, d, wY);
      const AsMin = Math.max(minFlexuralArea(p, m, cf.t, d, wY), 0);
      const AsNeeded = Math.max(fl.AsReq, AsMin);
      const dia = rc.bars.stemMain?.dia && rc.bars.stemMain.dia >= 16 ? rc.bars.stemMain.dia : 20;
      const n = Math.max(2, Math.ceil(AsNeeded / barArea(dia)));
      const AsProv = n * barArea(dia);
      const Veff = Math.max(0, Vd - (Md / d) * Math.tan(cf.beta));
      const shear = checkShear(p, m, Veff, cf.t, d, AsProv);
      const Mr = momentCapacity(m, AsProv, cf.s, d);
      const blockOk = fl.a <= rcStemThicknessAtY(g, y) + 1e-9;
      const ok = fl.ductile && shear.ok && Mr >= Md - 1e-9 && blockOk;
      if (!blockOk) notes.push(`Payanda y = ${fmt2(y)} m: basınç bloğu gövde kalınlığını aşıyor (a = ${fmt2(fl.a)} m)`);
      sections.push({
        id: `cf-${i}`, label: `Payanda y = ${fmt2(y)} m`, location: "counterfort", y, b: cf.s, h: wY, d, face: "back",
        Md, Vd: Veff, governingM: eff.caseM, governingV: eff.caseV, Mk: eff.Mk, Vk: eff.Vk,
        AsReq: fl.AsReq, AsMin, AsNeeded, AsProv, bar: { dia, spacing: 0, manual: false }, Mr, ratio: Mr > 0 ? Md / Mr : Infinity, cOverD: fl.cOverD, cOverDMax: fl.cOverDMax, ductile: fl.ductile, shear, ok,
        verdict: `${ok ? "SAĞLIYOR" : "SAĞLAMIYOR"} — M_d = ${fmt1(Md)} kNm, d = ${fmt2(d)} m → A_s = ${fmt0(AsNeeded)} mm², ${n}φ${dia} (${fmt0(AsProv)} mm²), M_r = ${fmt1(Mr)} kNm; V_eff = ${fmt1(Veff)} ${shear.ok ? "≤" : ">"} V_r = ${fmt1(shear.Vr)} kN`,
      });
    }
    // Bağlantı donatıları (çekme): yatay T_h = p_taban·s (gövde–payanda), düşey T_v = w·s (topuk–payanda); A_s = T/f_yd
    const Th = st0.p * cf.s, Tv = hl.w * cf.s;
    const AsTh = (Th / (m.fyd * 1000)) * 1e6 * (1 / m.phiFlexure), AsTv = (Tv / (m.fyd * 1000)) * 1e6 * (1 / m.phiFlexure);
    counterfort = {
      spacing: cf.s, thickness: cf.t, clearSpan: cf.L, alphaDeg: (cf.alpha * 180) / Math.PI,
      stemSlab: strips, verticalStrip: { a: aStrip, Md: effVertical.Md }, heelSlab: hl, sections, ties: { horizontal: { T: Th, As: AsTh }, vertical: { T: Tv, As: AsTv } },
      formulas: ["Gövde/topuk plağı: M⁻ = p·L²/12 (mesnet), M⁺ = p·L²/16 (açıklık), V = p·L/2; L = s − t (Huntington, Bowles Foundation Analysis and Design §12-9)",
        "Payanda: T kiriş konsol, M = M_konsol(y)·s, d = w(y)·sinα − pas payı; V_eff = V − (M/d)·tanβ (eğik çekme donatısı)",
        "Bağlantı: T_h = p·s (gövde–payanda yatay), T_v = w·s (topuk–payanda düşey), A_s = T/(φ·f_yd)"],
    };
    const cfMain = sections.reduce((a, b) => (b.AsNeeded > a.AsNeeded ? b : a), sections[0]!);
    if (cfMain) reinforcement0.push({ position: "counterfortMain", label: "Payanda ana (eğik kenar) donatısı", face: "eğik arka kenar", bar: { dia: cfMain.bar.dia, spacing: 0 }, count: Math.round(cfMain.AsProv / barArea(cfMain.bar.dia)), As: cfMain.AsProv, AsNeeded: cfMain.AsNeeded, manual: false, ok: sections.every((x) => x.ok), lb: p.developmentLength(m, cfMain.bar.dia).lb });
  }
  warn("RC_SHEAR_AT_FACE");

  // ── Kayma dişi (basitleştirilmiş)
  let key: RcSectionResult | null = null;
  if (g.key) {
    const list = designCases.map((dc) => { const k = keyEffects(input, g, cpOf(dc.caseId)); return { dc, Md: k.M * dc.combo.H, Vd: k.V * dc.combo.H }; });
    const eff = pick(list);
    key = mkSection("key", `Kayma dişi (taban seviyesi, d_k = ${fmt2(g.key.depth)} m)`, "key", "back", g.key.width, eff, "keyMain").sec;
  }

  // ── Donatı planı
  const hAvg = (f.tt + f.tb) / 2;
  const reinforcement: RcReinforcementItem[] = [];
  const secondary = (position: RcBarPosition, label: string, face: string, AsNeeded: number, h: number, note?: string): void => {
    const manual = manualBar(rc.bars, position);
    const sMax = p.maxSpacing(h).secondary;
    const c = manual ? { ...manualBars(manual, AsNeeded, sMax), manual: true } : { ...selectBars(AsNeeded, sMax), manual: false };
    const dia = c.dia;
    reinforcement.push({ position, label, face, bar: { dia, spacing: c.spacing }, As: c.As, AsNeeded, manual: c.manual, ok: c.ok, lb: p.developmentLength(m, dia).lb, ...(note ? { note } : {}) });
    if (manual) manualUsed.push(label);
  };
  reinforcement.push({ position: "stemMain", label: "Gövde düşey ana donatı", face: "arka yüz (dolgu)", bar: { dia: base.choice.dia, spacing: base.choice.spacing }, As: base.choice.As, AsNeeded: base.sec.AsNeeded, manual: base.choice.manual, ok: base.choice.ok && base.sec.ok, lb: lbMain, ...(cutoff ? { cutoff } : {}) });
  secondary("stemFront", "Gövde düşey ikincil donatı", "ön yüz", p.min.stemVerticalFace * hAvg * 1e6, hAvg);
  if (!isCf) secondary("stemHorizontal", "Gövde yatay (büzülme) donatısı", "her iki yüz", (p.min.stemHorizontalTotal * hAvg * 1e6) / 2, hAvg, `Toplam ρ = ${p.min.stemHorizontalTotal} iki yüze eşit bölündü; her yüz için çap/aralık`);
  for (const cs of cfSections) {
    const meta: Record<string, { pos: RcBarPosition; label: string; face: string }> = {
      "heel-support": { pos: "heelLongTop", label: "Topuk boyuna donatı (payanda mesnedi)", face: "üst yüz" }, "heel-span": { pos: "heelLongBottom", label: "Topuk boyuna donatı (açıklık)", face: "alt yüz" },
      "stemslab-support": { pos: "stemHorizontalSupport", label: "Gövde yatay donatı (payanda mesnedi)", face: "arka yüz (dolgu)" }, "stemslab-span": { pos: "stemHorizontalSpan", label: "Gövde yatay donatı (açıklık)", face: "ön yüz" },
    };
    const mt = meta[cs.sec.id]!;
    reinforcement.push({ position: mt.pos, label: mt.label, face: mt.face, bar: { dia: cs.choice.dia, spacing: cs.choice.spacing }, As: cs.choice.As, AsNeeded: cs.sec.AsNeeded, manual: cs.choice.manual, ok: cs.choice.ok && cs.sec.ok, lb: p.developmentLength(m, cs.choice.dia).lb });
  }
  reinforcement.push(...reinforcement0);
  if (counterfort) {
    secondary("counterfortTieH", "Gövde–payanda yatay bağlantı (çekme) donatısı", "payanda içinde, yatay", counterfort.ties.horizontal.As, f.tb, `T_h = ${fmt1(counterfort.ties.horizontal.T)} kN/m yükseklik`);
    secondary("counterfortTieV", "Topuk–payanda düşey bağlantı (çekme) donatısı", "payanda içinde, düşey", counterfort.ties.vertical.As, f.tf, `T_v = ${fmt1(counterfort.ties.vertical.T)} kN/m uzunluk`);
  }
  if (toe && toeChoice) reinforcement.push({ position: "toeMain", label: "Burun ana donatısı", face: toe.face === "bottom" ? "alt yüz" : "üst yüz", bar: { dia: toeChoice.dia, spacing: toeChoice.spacing }, As: toeChoice.As, AsNeeded: toe.AsNeeded, manual: toeChoice.manual, ok: toeChoice.ok && toe.ok, lb: p.developmentLength(m, toeChoice.dia).lb });
  if (heel && heelChoice) reinforcement.push({ position: "heelMain", label: "Topuk ana donatısı", face: heel.face === "top" ? "üst yüz" : "alt yüz", bar: { dia: heelChoice.dia, spacing: heelChoice.spacing }, As: heelChoice.As, AsNeeded: heel.AsNeeded, manual: heelChoice.manual, ok: heelChoice.ok && heel.ok, lb: p.developmentLength(m, heelChoice.dia).lb });
  const mainBase = Math.max(toe?.AsProv ?? 0, heel?.AsProv ?? 0);
  secondary("baseDistribution", "Taban boyuna (dağıtma) donatısı", "alt ve üst yüz", Math.max((p.min.baseDistributionTotal * f.tf * 1e6) / 2, p.min.distributionOfMain * mainBase), f.tf, "Her yüz için; ana donatının ≥ 1/5'i ve büzülme sınırı");
  if (key) reinforcement.push({ position: "keyMain", label: "Kayma dişi düşey donatısı", face: "arka yüz", bar: { dia: key.bar.dia, spacing: key.bar.spacing }, As: key.AsProv, AsNeeded: key.AsNeeded, manual: key.bar.manual, ok: key.ok, lb: p.developmentLength(m, key.bar.dia).lb });
  if (manualUsed.length) warn("RC_MANUAL_BARS", manualUsed.join(", "));

  if (code === "ec2" && alphaCc !== 1) notes.push(`α_cc = ${alphaCc} (${input.checks.ec.nationalAnnex} ulusal eki)`);
  notes.push(`Etkin derinlik d = h − pas payı (${fmt0(rc.cover * 1000)} mm) − φ/2`);
  const ok = stem.every((s) => s.ok) && (toe?.ok ?? true) && (heel?.ok ?? true) && (key?.ok ?? true) && reinforcement.every((r) => r.ok)
    && cfSections.every((x) => x.sec.ok) && (counterfort?.sections.every((x) => x.ok) ?? true);
  return {
    code, codeLabel: p.label, references: p.references, loadFactorNote: p.loadFactorNote,
    materials: m, cover: rc.cover, combinations: p.combinations(false, input.checks.ec.psi2).concat(cases.length > 1 ? p.combinations(true, input.checks.ec.psi2) : []),
    designCases: designCases.map((dc) => ({ id: dc.id, label: dc.label, caseId: dc.caseId, kind: dc.combo.kind, Nd: dc.Nd, eD: dc.eD, qToe: dc.qToe, qHeel: dc.qHeel, contactLength: dc.contactLength })),
    stem, toe, heel, key, reinforcement, ...(counterfort ? { counterfort } : {}), ok, notes,
    formulas: { flexure: base.sec ? designFlexure(p, m, 1, 1, 0.3, 0.35).formula : "", shear: p.shearResistance(m, 1, 0.3, 0.005).formula, development: p.developmentLength(m, 16).formula },
  };
}

function pick(list: { dc: DesignCase; Md: number; Vd: number }[]): Effects {
  let best = list[0]!;
  let bestV = list[0]!;
  for (const x of list) { if (Math.abs(x.Md) > Math.abs(best.Md)) best = x; if (Math.abs(x.Vd) > Math.abs(bestV.Vd)) bestV = x; }
  const z = (): ClassSums => ({ G: 0, Q: 0, H: 0, F: 0, E: 0 });
  return { Md: best.Md, Vd: Math.abs(bestV.Vd), caseM: best.dc.id, caseV: bestV.dc.id, Mk: z(), Vk: z() };
}

const rcStemThicknessAtY = (g: RcCantileverGeometry, y: number): number => stemThickness(g, y);
function stemThickness(g: RcCantileverGeometry, y: number): number {
  const f = rcFrame(g);
  const t = Math.min(1, Math.max(0, (y - f.tf) / f.hs));
  return f.tb + (f.tt - f.tb) * t;
}

const fmt0 = (v: number): string => fmt(v, 0);
const fmt1 = (v: number): string => fmt(v, 1);
const fmt2 = (v: number): string => fmt(v, 2);
const fmtCm = (s: number): string => `${fmt(s * 100, 1)} cm`;
const manualBar = (bars: WallInput["rc"]["bars"], pos: RcBarPosition): BarSpec | undefined => (pos in bars ? bars[pos as keyof typeof bars] : undefined);

export type { RcCodeProfile, RcCombination, RcMaterials };
